import { useLocation, useNavigate } from "react-router-dom";
import { AppImage as Image } from "@/shared/ui/AppImage";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { cn } from "@/shared/lib/cn";
import {
  ChevronDown,
  ChevronUp,
  Maximize2,
  MessageCircle,
  X,
} from "lucide-react";
import { useMessagingOptional } from "../../messaging/MessagingProvider";
import { ChatPanel } from "./ChatPanel";

export function FloatingChatWidget() {
  const messaging = useMessagingOptional();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const labels = t.safecheck.messaging;

  if (!messaging) return null;

  const {
    floatingState,
    selectedThread,
    threadMessages,
    draft,
    setDraft,
    sendMessage,
    collapseFloating,
    expandFloating,
    closeFloating,
    openFloating,
  } = messaging;

  const isOnMessagingPage = location.pathname === "/dashboard/messaging";
  const isVisible = !isOnMessagingPage && floatingState !== "hidden";

  if (!isVisible) return null;

  const handleMaximize = () => {
    navigate("/dashboard/messaging");
  };

  if (floatingState === "collapsed") {
    if (!selectedThread) return null;

    return (
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-end p-3 sm:p-4">
        <button
          type="button"
          onClick={expandFloating}
          className="pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 shadow-lg transition-colors hover:bg-card-muted/40"
          aria-label={labels.floating.expand}
        >
          {selectedThread.avatar ? (
            <Image
              src={selectedThread.avatar}
              alt={selectedThread.name}
              width={36}
              height={36}
              className="h-9 w-9 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
              {selectedThread.avatarFallback}
            </div>
          )}
          <div className="min-w-0 flex-1 text-left">
            <p className="truncate text-sm font-semibold text-foreground">
              {selectedThread.name}
            </p>
            <p className="truncate text-xs text-muted">{selectedThread.lastMessage}</p>
          </div>
          <ChevronUp size={18} className="shrink-0 text-muted" />
        </button>
      </div>
    );
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-end p-3 sm:p-4">
      <div
        className={cn(
          "pointer-events-auto flex w-full max-w-sm flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl",
          "h-[min(70vh,480px)]",
        )}
      >
        <div className="flex items-center gap-2 border-b border-border bg-primary px-3 py-2.5 text-white">
          <MessageCircle size={18} className="shrink-0" />
          <p className="min-w-0 flex-1 truncate text-sm font-semibold">
            {selectedThread?.name ?? labels.pageTitle}
          </p>
          <button
            type="button"
            onClick={handleMaximize}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-white/15"
            aria-label={labels.floating.maximize}
          >
            <Maximize2 size={16} />
          </button>
          <button
            type="button"
            onClick={collapseFloating}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-white/15"
            aria-label={labels.floating.collapse}
          >
            <ChevronDown size={18} />
          </button>
          <button
            type="button"
            onClick={closeFloating}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-white/15"
            aria-label={labels.floating.close}
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1">
          {selectedThread ? (
            <ChatPanel
              thread={selectedThread}
              messages={threadMessages}
              draft={draft}
              labels={labels.chat}
              compact
              onDraftChange={setDraft}
              onSend={sendMessage}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-muted">{labels.chat.selectConversation}</p>
              <button
                type="button"
                onClick={() => {
                  openFloating();
                  navigate("/dashboard/messaging");
                }}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
              >
                {labels.floating.openInbox}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
